// This must be in scope of a reference provider
import {CSSProperties, useContext, useId} from "react";
import {ReferenceContext} from "../ReferenceProvider";
import {Form} from "react-bootstrap";
import {t} from "ttag";
import Select from "react-select";
import {CustomTopic, QuestionInputConfig} from "../../javaTypes";
import './TopicRuleGroup.css'
import {Section} from "first-common/javaTypes";

export function TopicSort (a?: Section | string, b?: Section | string){
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    /*
        This is up to 3 parted number - exponentiate each by a successively smaller exponent and multiply by the part
            Use 5 to make buffer in case a 5-part section shows up
        Use 100 as the base because > 10 sections/subsections possible
        Sum the resulting value from each component (p + ... )
     */
    const reducer = (p: number, c: number, i: number) => p + c * (100 ** (5 - i));
    const isSectionString = (s: string) => s.charAt(0) >= '0' && s.charAt(0) <= '9';
    if (typeof a === 'string' && !isSectionString(a)) {
        if (typeof b === 'string' && !isSectionString(b)) {
            // both are custom topics - return lex sort
            return a.localeCompare(b);
        }
        // b is a section - put a first
        return -1;
    } else if (typeof b === 'string' && !isSectionString(b)) {
        // b is custom, a is a section, put b first
        return 1;
    }
    const aParts = (typeof a === 'string' ? a : a.id).split(".").map(x => parseInt(x)).reduce(reducer, 0);
    const bParts = (typeof b === 'string' ? b : b.id).split(".").map(x => parseInt(x)).reduce(reducer, 0);
    if (isNaN(aParts)) return 1;
    if (isNaN(bParts)) return -1;
    return aParts - bParts;
}

export function getTagRequirementLevel(config: QuestionInputConfig) {
    if (config === QuestionInputConfig.REQUIRED) return "(" + t`required` + ")";
    if (config === QuestionInputConfig.OPTIONAL) return "(" + t`optional` + ")";
}

export default function TopicEditor({config, topic, setTopic, style}: {
    config: QuestionInputConfig,
    topic: string,
    setTopic: (topic: string) => void,
    style?: CSSProperties
}) {
    const references = useContext(ReferenceContext);
    const id = useId();
    const topics = references?.sections ? [...Object.values(references.customTopics), ...Object.values(references.sections).sort(TopicSort)] : [];
    function isSection(s: Section | CustomTopic): s is Section {
        return (s as Section).id !== undefined;
    }
    return <Form.Group controlId={id} style={Object.assign({}, style ?? {}, {marginTop: '1ch'})}>
        <Form.Label style={{position: 'relative', top: '.75ch'}}>{t`Topic/Section`} {getTagRequirementLevel(config)}</Form.Label>
        {/* onChange is always a MultiValue<Section> type param but that's not what it actually gets when multi is off - it is just the singular object*/}
        <Select value={topics.find(x => isSection(x) ? x.id === topic : x.label === topic)} onChange={e => setTopic(e ? (isSection(e) ? e.id : e.label) : "")}
                     options={topics} getOptionValue={t => isSection(t) ? t.id : t.label} isClearable={true} escapeClearsValue={true}
                     getOptionLabel={t => isSection(t) ? (t.id + " - " + t.name) : t.name}
                     styles={{option: (b, s) => ({...b, fontSize: '.9rem', padding: "4px 8px"})}}

        />
    </Form.Group>
}